import React, { Component, useRef } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import moment from 'moment';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { timestampToDate } from '../../../util/dates';
import { isStringAndNotDate, propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';

import { FieldSelect, FieldTextInput, Form, H6, IconClose, PrimaryButton, IconAdd, Modal, FieldCheckboxGroup, FieldRadioButton, NamedLink } from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import FieldDateAndTimeInput from './FieldDateAndTimeInput';

import css from './BookingTimeForm.module.css';
import { SEATS_PREFIX } from '../../../util/userHelpers';
import FieldDateInput from '../../FieldDateInput/FieldDateInput';
import { getOrdinalSuffix, isStringAndNotEmpty, ucfirst } from '../../../util/string';
import { uploadFilesFirebase } from '../../../util/api';
import { cloneDeep } from 'lodash';
import { formatMoney } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';

const { Money } = sdkTypes;

export class BookingTimeFormComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedProducts: [{ name: '', price: '' }],
      selectedServices: [{ name: '', price: '' }],
      formComponent: null,
      seatIndex: null,
    }

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);

    this.setModalForm = this.setModalForm.bind(this);
    this.closeModalForm = this.closeModalForm.bind(this);
  }

  handleFormSubmit(e) {
    const additionalProducts = e.additionalProducts ? e.additionalProducts.map(product => {
      return {
        name: product.name,
        price: this.props.additionalProducts.find(p => p.name === product.name).price,
      }
    }) : [];

    const additionalServices = e.additionalServices ? e.additionalServices.map(service => {
      return {
        name: service.name,
        price: this.props.additionalServices.find(s => s.name === service.name).price,
      }
    }) : [];

    let seatsDates = {};
    Array.from({ length: Number(e.seats) }).map((_, seatIndex) => {
      let seatDateIndex = `${SEATS_PREFIX}_${seatIndex}_lastDive`;
      if (e[seatDateIndex] !== undefined && e[seatDateIndex] !== null) {
        let val = e[seatDateIndex];
        if (val && !!val?.date && val.date instanceof Date && !isNaN(val.date)) {
          val = moment(val.date).format('YYYY-MM-DD');
          seatsDates[seatDateIndex] = val;
        }
      }
    });

    this.props.onSubmit({...e, ...seatsDates,additionalProducts, additionalServices});
  }

  // When the values of the form are updated we need to fetch
  // lineItems from this template's backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the orderData object.
  handleOnChange(formValues) {
    const { bookingStartTime, bookingEndTime, seats: seatsRaw, gear } = formValues.values;
    const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
    const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;
    const seats = parseInt(seatsRaw, 10);

    const listingId = this.props.listingId;
    const isOwnListing = this.props.isOwnListing;

    // Note: we expect values bookingStartTime and bookingEndTime to be strings
    // which is the default case when the value has been selected through the form
    const isStartBeforeEnd = bookingStartTime < bookingEndTime;

    const additionalProducts = formValues.values.additionalProducts ? formValues.values.additionalProducts.map(product => {
      return {
        name: product.name,
        price: this.props.additionalProducts.find(p => p.name === product.name).price,
      }
    }) : [];

    const additionalServices = formValues.values.additionalServices ? formValues.values.additionalServices.map(service => {
      return {
        name: service.name,
        price: this.props.additionalServices.find(s => s.name === service.name).price,
      }
    }) : [];

    const seatsGear = Array.from({ length: Number(seats) }).map((_, seatIndex) => {
      const gearKey = `${SEATS_PREFIX}_${seatIndex}_gear`;
      return formValues.values[gearKey] || 'full';
    });

    const countMap = {};
    Array.from({ length: Number(seats) }).map((_, seatIndex) => {
      const serviceKey = `${SEATS_PREFIX}_${seatIndex}_additionalServices`;
      return formValues.values[serviceKey] || [];
    }).flat().forEach(item => {
      countMap[item] = (countMap[item] || 0) + 1;
    });

    const seatsAdditionalServices = Object.keys(countMap || []).map(key => ({
      name: key,
      count: countMap[key]
    }));

    if (
      bookingStartTime &&
      bookingEndTime &&
      isStartBeforeEnd &&
      !this.props.fetchLineItemsInProgress
    ) {
      this.props.onFetchTransactionLineItems({
        orderData: { bookingStart: startDate, bookingEnd: endDate, seats, seatsGear, seatsAdditionalServices, additionalProducts, additionalServices, gear },
        listingId,
        isOwnListing,
      });
    }
  }

  setModalForm(formComponent, seatIndex) {
    this.setState({ formComponent, seatIndex });
  }

  closeModalForm(form) {
    let values = form?.getState()?.values || {};
    this.setState({ formComponent: null, seatIndex: null });
    this.handleOnChange({ values });
  }

  render() {
    const {
      rootClassName,
      className,
      price: unitPrice,
      dayCountAvailableForBooking,
      marketplaceName,
      ...rest
    } = this.props;
    const classes = classNames(rootClassName || css.root, className);

    return (
      <FinalForm
        {...rest}
        mutators={{ ...arrayMutators }}
        unitPrice={unitPrice}
        onSubmit={this.handleFormSubmit}
        render={fieldRenderProps => {
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            form,
            pristine,
            handleSubmit,
            intl,
            isOwnListing,
            listingId,
            values,
            monthlyTimeSlots,
            onFetchTimeSlots,
            timeZone,
            lineItems,
            fetchLineItemsInProgress,
            fetchLineItemsError,
            payoutDetailsWarning,
            additionalServices,
            additionalProducts,
            seatsFields,
            isAuthenticated
          } = fieldRenderProps;

          const startTime = values && values.bookingStartTime ? values.bookingStartTime : null;
          const endTime = values && values.bookingEndTime ? values.bookingEndTime : null;
          const startDate = startTime ? timestampToDate(startTime) : null;
          const endDate = endTime ? timestampToDate(endTime) : null;

          // This is the place to collect breakdown estimation data. See the
          // EstimatedCustomerBreakdownMaybe component to change the calculations
          // for customized payment processes.
          const breakdownData =
            startDate && endDate
              ? {
                startDate,
                endDate,
              }
              : null;

          const showEstimatedBreakdown =
            breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

          const fieldsToWatch = ['bookingEndDate', 'bookingEndTime', 'bookingStartDate', 'bookingStartTime', 'seats', 'additionalProducts', 'additionalServices'];
          const prevValuesRef = useRef({});

          let modalFormDisabled = true;
          if (!!this.state.formComponent && this.state.seatIndex != null) {
            let dynamicSeatsFields = cloneDeep(seatsFields).filter(f => {
              if (f.visible && typeof f.visible === 'function' && f.compareName && isStringAndNotEmpty(f.compareName)) {
                return f.visible(values[`${SEATS_PREFIX}_${this.state.seatIndex}_${f.compareName}`])
              }
              return !!f.validate;
            });

            let filledValues = dynamicSeatsFields.map(f => {
              let varName = `${SEATS_PREFIX}_${this.state.seatIndex}_${f.name}`;
              return !!values[varName];
            }).filter(f => f);

            modalFormDisabled = filledValues.length < dynamicSeatsFields.length;
          }

          let bookingDisabled = true;
          let seatsCompleted = Array.from({ length: Number(values.seats) }).map((_, seatIndex) => {
            let generalDynamicSeatsFields = cloneDeep(seatsFields).filter(f => {
              if (f.visible && typeof f.visible === 'function' && f.compareName && isStringAndNotEmpty(f.compareName)) {
                return f.visible(values[`${SEATS_PREFIX}_${seatIndex}_${f.compareName}`])
              }
              return !!f.validate;
            });

            let filledValues = generalDynamicSeatsFields.map(f => {
              let varName = `${SEATS_PREFIX}_${seatIndex}_${f.name}`
              return !!values[varName];
            }).filter(f => f);

            return filledValues.length == generalDynamicSeatsFields.length
          }).filter(s => s);

          bookingDisabled = seatsCompleted.length < Number(values.seats);

          const formItemEl = (seatsField, seatIndex, CertificationCardInput = null) => {
            let additionalProps = {};
            let Component = null;
            switch (seatsField.type) {
              case 'text':
              case 'number':
              case 'email':
              case 'date':
                Component = FieldTextInput;
                break;
              case 'select':
                Component = FieldSelect;
                break;
              case 'datepicker':
                Component = FieldDateInput;
                break;
              case 'radio':
                let radioValuePath = seatsField.keyLabel ? seatsField.keyLabel : 'id';

                return <div className={css.radioContainer} key={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}`}>
                  <label>{seatsField.label}</label>
                  <div>
                    {seatsField.options.map((opt, optIndex) => (
                      <FieldRadioButton
                        key={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}_${optIndex}`}
                        id={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}_${optIndex}`}
                        name={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}`}
                        value={opt[radioValuePath]}
                        label={opt.label}
                      />
                    ))}
                  </div>
                </div>;
              case 'checkboxGroup':
                Component = FieldCheckboxGroup;
                additionalProps = {
                  options: seatsField?.options || []
                };
                break;
              case 'certificationCard':
                Component = CertificationCardInput;
                break;
            }

            if (!Component) {
              return null;
            }

            let validate = seatsField.validate ? seatsField.validate : null;

            let placeholderProp = !['select'].includes(seatsField.type) ? { placeholder: seatsField.label} : {};

            return (
              <Component
                key={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}`}
                id={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}`}
                name={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}`}
                className={css.input}
                validate={validate}
                label={seatsField.label}
                {...placeholderProp}
                {...additionalProps}
                {...seatsField.otherProps}
              >
                {seatsField?.type == 'select' && seatsField?.options && Array.isArray(seatsField.options) ? <>
                  <option disabled key={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}_disabled`} value="">{seatsField.label}</option>
                  {seatsField.options.map(seatsFieldOption => {
                    let valuePath = seatsField.keyLabel ? seatsField.keyLabel : 'id';
                    return (
                      <option key={`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}_${seatsFieldOption[valuePath]}`} value={seatsFieldOption[valuePath]}>
                        {seatsFieldOption.label}
                      </option>
                    );
                  })} </> : null}
              </Component>);
          }

          return (
            <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
              <FormSpy
                subscription={{ values: true }}
                onChange={formValues => {
                  const currentValues = formValues.values;
                  const prevValues = prevValuesRef.current;
                  const birthdateName = `${SEATS_PREFIX}_0_birthdate`;

                  const changedField = Object.keys(currentValues).find(
                    (key) => currentValues[key] !== prevValues[key]
                  );

                  let serviceChanged = changedField?.startsWith(`${SEATS_PREFIX}_`) && changedField?.endsWith('_additionalServices');

                  if (this.state.seatIndex !== null) {
                    let gearField = seatsFields.find(f => f.name == 'gear');
                    let gearName = `${SEATS_PREFIX}_${this.state.seatIndex}_gear`;
                    if (gearField && gearField?.otherProps?.onChange && currentValues[gearName] !== prevValues[gearName]) {
                      gearField.otherProps.onChange(currentValues[gearName]);
                    }
                  }

                  if (isStringAndNotDate(currentValues[birthdateName])) {
                    // fieldRenderProps.form.change(birthdateName, {date: new Date(currentValues[birthdateName])});
                  }

                  Array.from({ length: Number(parseInt(currentValues.seats)) }).forEach((_, i) => {
                    const gearKey = `${SEATS_PREFIX}_${i}_gear`;
                    if (!isStringAndNotEmpty(currentValues[gearKey])) {
                      fieldRenderProps.form.change(gearKey, 'full');
                    }
                  });

                  const relevantValuesChanged = fieldsToWatch.some(field => {
                    const path = field.split('.');
                    const currentValue = path.reduce(
                      (acc, key) => acc && acc[key],
                      currentValues
                    );
                    const previousValue = path.reduce(
                      (acc, key) => acc && acc[key],
                      prevValues
                    );
                    return currentValue !== previousValue;
                  });

                  if (relevantValuesChanged || serviceChanged) {
                    this.handleOnChange(formValues);
                  }

                  prevValuesRef.current = currentValues;
                }}
              />

              {monthlyTimeSlots && timeZone ? (
                <FieldDateAndTimeInput
                  startDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingStartTitle' }),
                    placeholderText: startDatePlaceholder,
                  }}
                  endDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingEndTitle' }),
                    placeholderText: endDatePlaceholder,
                  }}
                  className={css.bookingDates}
                  listingId={listingId}
                  onFetchTimeSlots={onFetchTimeSlots}
                  monthlyTimeSlots={monthlyTimeSlots}
                  values={values}
                  intl={intl}
                  form={form}
                  pristine={pristine}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                />
              ) : null}

              <Modal
                id="seats.profile"
                isOpen={!!this.state.formComponent}
                onClose={() => this.closeModalForm(form)}
                usePortal
                onManageDisableScrolling={this.props.onManageDisableScrolling}
              >
                {this.state.formComponent}
                <PrimaryButton className={css.modalFormButton} disabled={modalFormDisabled} onClick={() => this.closeModalForm(form)}>Save</PrimaryButton>
              </Modal>

              { Number(values.seats) >= 1 && values?.bookingStartDate ? isAuthenticated ?
                Array.from({ length: Number(values.seats) }).map((_, seatIndex) => {
                  const CertificationCardInput = (props) => {
                    let gearInitialOption = values[`${SEATS_PREFIX}_${seatIndex}_gear`];
                    let initialStyle = {
                      maxWidth: 'unset',
                      overflow: 'unset',
                      display: ['full', 'partial'].includes(gearInitialOption) ? 'unset' : 'none'
                    };

                    return <div id="fileInputWrapper" style={initialStyle} className={classNames(css.fileInputWrapper)}>
                      <label style={{ margin: "16px 0 0" }}>{props.label}</label>
                      <input type="file" id={props.id} name={props.name} className={css.fileInput} onChange={(e) => {
                        uploadFilesFirebase(e.target.files[0], 'certification-card').then((res) => {
                          if (res.success) {
                            fieldRenderProps.form.change(props.name, res.url);
                          }
                        });
                      }} />
                      <label htmlFor="certificationCard" className={classNames(css.fileInputLabel, {
                        [css.fileInputLabelSuccess]: values[props.name]
                      })}>
                        {values[props.name] ? props.updatedLabel : props.updateLabel}
                      </label>
                    </div>
                  };

                  let formComponent = <>
                    <div key={seatIndex}>
                      <div className={css.input}>{getOrdinalSuffix(seatIndex + 1)} Diver</div>
                      {seatsFields.filter(s => !s.hideOnModal).map((seatsField, sf_k) => formItemEl(seatsField, seatIndex, CertificationCardInput))}
                    </div>
                  </>;

                  return <div
                    key={`detail_${seatIndex}`}
                    className={css.detailSeat}
                    // onClick={() => this.setModalForm(formComponent, seatIndex)}
                  >
                    <div className={css.detailIndex} onClick={() => this.setModalForm(formComponent, seatIndex)}>
                      <div>{getOrdinalSuffix(seatIndex + 1)} Diver</div>
                      <div>Click here to edit this seat</div>
                    </div>
                    {seatsFields.map((seatsField, sf_k) => {
                      if (seatsField.visible !== undefined && typeof seatsField.visible === 'function') {
                        let compareValue = values[`${SEATS_PREFIX}_${seatIndex}_${seatsField.compareName}`];
                        if (!seatsField.visible(compareValue)) {
                          return null;
                        }
                      }
                      let presentValue = values[`${SEATS_PREFIX}_${seatIndex}_${seatsField.name}`];
                      if (seatsField.showAsLabel || seatsField.printedLabel) {
                        let selectedOption = (seatsField.options || []).find(opt => opt[seatsField.keyLabel || 'id'] == presentValue);
                        if (selectedOption) {
                          presentValue = seatsField.printedLabel ? (selectedOption.printedLabel || selectedOption.label) : selectedOption.label;
                        }
                      }
                      if (seatsField.type == 'checkboxGroup') {
                        presentValue = (!!presentValue && Array.isArray(presentValue) && presentValue.length) ? presentValue.map(v => {
                          let currentLabel = seatsField.options.find(o => o.key == v);
                          if (currentLabel) {
                            return currentLabel.label;
                          }

                          return v;
                        }).join(', ') : '-';
                      }
                      let hasValue = !!presentValue;
                      let val = presentValue || 'Please, edit this field';
                      // if (val && !!val?.date && val.date instanceof Date && !isNaN(val.date)) {
                      //   val = val.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                      // }

                      if (val && !seatsField.removeCapitalize) {
                        val = ucfirst(val);
                      }

                      let labelKey = `${SEATS_PREFIX}_detail_label_${seatIndex}_${seatsField.name}`;

                      if (seatsField.showAs && typeof seatsField.showAs === 'function') {
                        val = seatsField.showAs(hasValue);
                      }

                      if (seatsField.hideOnModal) {
                        return (<div key={sf_k} className={css.seatIndividualDataForm}>
                          <div key={labelKey} className={css.detailLabel}>{seatsField.label}</div>
                          <div>{formItemEl(seatsField, seatIndex)}</div>
                        </div>);
                      }

                      return (<div key={sf_k} className={css.seatIndividualData}>
                        <div className={css.detailLabel}>{seatsField.label}</div>
                        <div className={classNames(css.detailValue, {[css.pendingValue]: !hasValue})}>{val}</div>
                      </div>);
                    })}
                  </div>
                })
              : 
              <NamedLink
                name="LoginPage"
                className={css.primaryButtonRoot}
                to={{
                  state: { from: window?.location.pathname || null },
                }}
              >
                <FormattedMessage id="BookingTimeForm.loginLink" />
              </NamedLink> : null}

              {isAuthenticated && false && values?.bookingStartDate && additionalServices && additionalServices.length > 0 ? (
                <div className={css.additionalServicesWrapper}>
                  <h4 className={css.sectionTitle}>
                    Additional Services
                    <IconAdd className={css.additionalServicesInfo} onClick={() => {
                      // Add a new service to the selectedServices array
                      this.setState({ selectedServices: [...this.state.selectedServices, { name: '', price: '' }] });
                    }} />
                  </h4>
                  {this.state.selectedServices.map((service, index) => (
                    <div key={index} className={css.additionalService}>
                      <FieldSelect
                        id={`additionalServices.${index}.name`}
                        name={`additionalServices.${index}.name`}
                        className={css.additionalServicesSelect}
                      >
                        <option value="">
                          {intl.formatMessage({ id: 'BookingTimeForm.additionalServicesPlaceholder' })}
                        </option>
                        {additionalServices.map((service, index) => (
                          <option key={index} value={service.name}>
                            {service.name} - {formatMoney(intl, new Money(service.price.amount, service.price.currency))}
                          </option>
                        ))}
                      </FieldSelect>
                      <IconClose rootClassName={classNames(css.closeIcon, {
                        [css.additionalServiceCloseIcon]: index === 0
                      })} onClick={() => {
                        if (index !== 0) {
                          // Remove the service by index from the selectedServices array
                          let newSelectedServices = [...this.state.selectedServices]
                          newSelectedServices.splice(index, 1);

                          this.setState({ selectedServices: newSelectedServices });
                        }
                      }}/>
                    </div>
                  ))}
                </div>
              ) : null}

              {isAuthenticated && showEstimatedBreakdown ? (
                <div className={css.priceBreakdownContainer}>
                  <H6 as="h3" className={css.bookingBreakdownTitle}>
                    <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
                  </H6>
                  <hr className={css.totalDivider} />
                  <EstimatedCustomerBreakdownMaybe
                    breakdownData={breakdownData}
                    lineItems={lineItems}
                    timeZone={timeZone}
                    currency={unitPrice.currency}
                    marketplaceName={marketplaceName}
                    processName={BOOKING_PROCESS_NAME}
                  />
                </div>
              ) : null}

              {fetchLineItemsError ? (
                <span className={css.sideBarError}>
                  <FormattedMessage id="BookingTimeForm.fetchLineItemsError" />
                </span>
              ) : null}

              {isAuthenticated ?
              <div className={css.submitButton}>
                <PrimaryButton type="submit" disabled={bookingDisabled} inProgress={fetchLineItemsInProgress}>
                  <FormattedMessage id="BookingTimeForm.requestToBook" />
                </PrimaryButton>
              </div>
              : null}

              <p className={css.finePrint}>
                {payoutDetailsWarning ? (
                  payoutDetailsWarning
                ) : (
                  <FormattedMessage
                    id={
                      isOwnListing
                        ? 'BookingTimeForm.ownListing'
                        : 'BookingTimeForm.youWontBeChargedInfo'
                    }
                  />
                )}
              </p>
            </Form>
          );
        }}
      />
    );
  }
}

BookingTimeFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  listingId: null,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingTimeFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,

  dayCountAvailableForBooking: number.isRequired,
};

const BookingTimeForm = compose(injectIntl)(BookingTimeFormComponent);
BookingTimeForm.displayName = 'BookingTimeForm';

export default BookingTimeForm;
